"use client";

import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import type { DepartureDto } from "@tripcart/shared";
import { Button } from "@tripcart/traveler-ui/components/button";

import { useFormatDate } from "@/lib/datetime-format-context";
import { usePluralTranslator, useTranslator } from "@/lib/translation-context";

import { TourSection } from "../_tour-section";
import { endDateFor } from "../_trip-end-date";
import {
  DepartureFilters,
  type DepartureSort,
} from "./departure-filters.client";
import { DepartureRow } from "./departure-row";
import type { DepartureExtras, DepartureFacts } from "./departure-details";
import {
  availableSorts,
  filterAndSort,
  groupByYearMonth,
} from "./departure-list.logic";
import styles from "./departures-section.module.css";

/** How many rows before "Show more departures". The guide's layout 12 pages. */
const PAGE = 6;

interface DeparturesSectionProps {
  /** Live departures, or null when the availability window has not landed yet
   *  (or failed). Null is NOT an empty schedule — see the empty state below. */
  departures: DepartureDto[] | null;
  /** Trip length in days, for the end date on each row. Null when the tour
   *  carries no duration. */
  durationDays: number | null;
  /** Operator's deposit percent, or null when they have set no payment policy. */
  depositPercent: number | null;
  /** Everything the expanded "See more details" panel shows. */
  facts: DepartureFacts;
  /** The operator's own detail lines and the two WhatsApp surfaces, each
   *  absent unless it should render. Replaced a single pre-built `whatsappUrl`
   *  for the whole page: the link is now built per row, because the greeting
   *  keeps its `{date}` placeholder. */
  extras?: DepartureExtras | undefined;
  /** The smallest party the operator will run, from the active package's
   *  `minParticipants`. Null when they set none, which means no departure can
   *  be called guaranteed — see `isGuaranteedToRun`. Deliberately NOT
   *  `groupSize.min`: the API builds `groupSize` as a hardcoded literal. */
  minParticipants: number | null;
  /** False for an on-request tour, which by definition does not confirm
   *  instantly. Drives the promise ribbon — see `PromiseBadges`. */
  instantConfirmation: boolean;
  /** True when the API says the schedule continues past the window we hold. */
  hasMoreAfter: boolean;
  onSelectDate: (date: Date) => void;
  formatDate: (iso: string) => string;
  /** Quote-on-request (AC-10): passed straight through to each row — see
   *  `departure-row.tsx` for what it hides and replaces. */
  isOnRequest?: boolean;
  /** Replaces "Book now" on every row when `isOnRequest` is true. */
  onRequestQuote?: () => void;
}

export function DeparturesSection({
  departures,
  durationDays,
  depositPercent,
  facts,
  extras,
  minParticipants,
  instantConfirmation,
  hasMoreAfter,
  onSelectDate,
  formatDate,
  isOnRequest,
  onRequestQuote,
}: DeparturesSectionProps) {
  const t = useTranslator();
  const tn = usePluralTranslator();
  const formatMonth = useFormatDate();
  const [year, setYear] = useState<number | null>(null);
  const [month, setMonth] = useState<number | "all">("all");
  const [sort, setSort] = useState<DepartureSort>("date");
  const [shown, setShown] = useState(PAGE);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { years, monthsByYear } = useMemo(
    () => groupByYearMonth(departures ?? []),
    [departures],
  );
  // Default to the first year that has anything, not to "now" — a schedule
  // that only starts next season would otherwise open on an empty tab.
  const activeYear = year ?? years[0] ?? new Date().getFullYear();

  // Which sort chips this schedule can actually reorder by. Judged over the
  // WHOLE schedule, not the visible month, so the row does not appear and
  // disappear as the traveller steps through months.
  const sorts = useMemo(() => availableSorts(departures ?? []), [departures]);
  // A sort the schedule no longer offers would leave the list ordered by a key
  // with no chip to show for it.
  const activeSort = sorts.includes(sort) ? sort : "date";

  const visible = useMemo(
    () =>
      filterAndSort(departures ?? [], {
        year: activeYear,
        month,
        sort: activeSort,
      }),
    [departures, activeYear, month, activeSort],
  );

  // Loading and "genuinely nothing scheduled" are DIFFERENT and must not share
  // a message. `null` means the window has not answered; telling someone a
  // tour has no departures when we simply have not looked is the same class of
  // error as calling an unknown date sold out.
  if (departures === null) {
    return (
      <TourSection id="dates" title={t("Dates & prices")}>
        <p className={styles.note}>
          {t("Checking which dates are still open…")}
        </p>
      </TourSection>
    );
  }

  if (departures.length === 0) {
    return (
      <TourSection
        id="dates"
        title={t("Dates & prices")}
        description={t("No departures are scheduled for this tour yet.")}
      >
        <p className={styles.note}>
          {t("Ask us about a private date and we will arrange one.")}
        </p>
      </TourSection>
    );
  }

  const page = visible.slice(0, shown);
  // Agency formatter — see the note on `DateTile`. This string names the same
  // month the tabs above it name, so the two must not come from different
  // locales, and this component is SSR'd on the `(public)` route.
  const monthLabel =
    month === "all"
      ? String(activeYear)
      : formatMonth(new Date(activeYear, month, 1), {
          monthStyle: "long",
          omitDay: true,
        });

  // No section description: the operator has no field that writes one, so a
  // fixed sentence here is copy nobody on the agency's side can change.
  return (
    <TourSection id="dates" title={t("Dates & prices")}>
      <DepartureFilters
        years={years}
        activeYear={activeYear}
        onYear={(y) => {
          setYear(y);
          setMonth("all");
          setShown(PAGE);
        }}
        months={monthsByYear.get(activeYear) ?? []}
        activeMonth={month}
        onMonth={(m) => {
          setMonth(m);
          setShown(PAGE);
        }}
        sort={activeSort}
        onSort={(s) => {
          setSort(s);
          setShown(PAGE);
        }}
        sorts={sorts}
        t={t}
      />

      {page.length === 0 ? (
        <p className={styles.empty}>
          {t(
            "No departures in this month. Try another, or ask us about a private date.",
          )}
        </p>
      ) : (
        <div className={styles.list}>
          {page.map((departure) => (
            <DepartureRow
              key={departure.id}
              departure={departure}
              endDate={endDateFor(departure.date, durationDays)}
              selected={false}
              expanded={expanded === departure.id}
              onToggleDetails={() =>
                setExpanded(expanded === departure.id ? null : departure.id)
              }
              onBook={() =>
                onSelectDate(new Date(`${departure.date}T00:00:00`))
              }
              depositPercent={depositPercent}
              facts={facts}
              extras={extras}
              minParticipants={minParticipants}
              instantConfirmation={instantConfirmation}
              formatDate={formatDate}
              tn={tn}
              t={t}
              isOnRequest={isOnRequest}
              onRequestQuote={onRequestQuote}
            />
          ))}
        </div>
      )}

      {/* The guide's `.dep-foot`: a centred stack, button over count, 8px
          apart and 24px below the list — not the left/right split this was.
          A centred grid rather than a flex row, so the count keeps its own
          line and stays centred under the button whatever its length. */}
      <div className={styles.foot}>
        {shown < visible.length ? (
          // Guide `.dep-more`: a 46px DASHED brand pill, not a text link. The
          // dash is what marks it as "there is more behind this" rather than a
          // committing action; it goes solid on hover, which is the guide's own
          // treatment.
          <Button
            variant="outline"
            size="lg"
            shape="round"
            onClick={() => setShown(shown + PAGE)}
            className={styles.more}
          >
            {t("Show more departures")}
            <ChevronDown aria-hidden strokeWidth={2.2} />
          </Button>
        ) : null}
        <span className={styles.count}>
          {t("Showing {shown} of {total} departures in {where}.", {
            shown: page.length,
            total: visible.length,
            where: monthLabel,
          })}
          {/* An explicit note, not silence: the window we hold is not the
              whole schedule, and a count that looks complete when it is not
              is the "no silent caps" failure. */}
          {hasMoreAfter ? ` ${t("More dates open further ahead.")}` : ""}
        </span>
      </div>
    </TourSection>
  );
}
